import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { NonNegativePaiseSchema } from './money.js';
/**
 * Enums shared across the API, the admin panel and the marketing site.
 *
 * These mirror the Prisma enums by hand rather than importing the generated
 * client: the marketing site must not pull a database client into its bundle,
 * and the admin panel should not need Prisma to render a status chip. The
 * drift test in `packages/db` fails the build if the two lists diverge.
 */
export const UserRoleSchema = z.enum([
    'OWNER',
    'TENANT',
    'FAMILY_MEMBER',
    'COMMITTEE_MEMBER',
    'SOCIETY_ADMIN',
    'GUARD',
    'STAFF',
]);
export const PlatformRoleSchema = z.enum(['SUPER_ADMIN', 'SUPPORT', 'BILLING', 'READ_ONLY']);
/**
 * The subscription state machine, as the API enforces it.
 *
 * The transition table lives in `apps/api/src/modules/subscriptions`; this is
 * the vocabulary both ends share. `GRACE` and `PAST_DUE` are distinct on
 * purpose: one is "the payment failed", the other is "we are still trying",
 * and a society admin needs to be told which.
 */
export const SubscriptionStatusSchema = z.enum([
    'TRIAL',
    'ACTIVE',
    'PAST_DUE',
    'GRACE',
    'SUSPENDED',
    'CANCELLED',
]);
/** Whether a society in this state accepts writes. */
export const allowsWrites = (status) => status === 'ACTIVE' || status === 'TRIAL';
export const PlanCodeSchema = z.enum(['STARTER', 'GROWTH', 'PRO', 'ENTERPRISE']);
/**
 * Every gateable capability.
 *
 * A route may require both a plan feature *and* a runtime flag — "AI triage is
 * in Pro" and "AI triage is rolled out to 10% of Pro societies" are different
 * questions with different owners.
 */
export const FeatureKeySchema = z.enum([
    'announcements',
    'bill_payments',
    'complaints',
    'visitor_management',
    'amenity_booking',
    'documents',
    'sos',
    'accounting',
    'chat',
    'marketplace',
    'paid_events',
    'daily_helpers',
    'smart_cards',
    'move_in_out',
    'multi_block',
    'bank_reconciliation',
    'utility_tracker',
    'asset_register',
    'custom_reports',
    'ai_triage',
    'iot_devices',
    'priority_support',
    'white_label',
]);
export const VisitorTypeSchema = z.enum([
    'GUEST',
    'DELIVERY',
    'CAB',
    'SERVICE',
    'HELPER',
    'VENDOR',
    'OTHER',
]);
export const VisitStatusSchema = z.enum([
    'PENDING',
    'APPROVED',
    'DENIED',
    'CHECKED_IN',
    'CHECKED_OUT',
    'LEFT_AT_GATE',
    'EXPIRED',
]);
export const ComplaintStatusSchema = z.enum([
    'OPEN',
    'ASSIGNED',
    'IN_PROGRESS',
    'AWAITING_RESIDENT',
    'RESOLVED',
    'CLOSED',
    'REOPENED',
    'REJECTED',
]);
export const ComplaintPrioritySchema = z.enum(['LOW', 'NORMAL', 'HIGH', 'EMERGENCY']);
export const BillStatusSchema = z.enum([
    'UNPAID',
    'PARTIALLY_PAID',
    'PAID',
    'OVERDUE',
    'WAIVED',
    'PENDING_APPROVAL',
]);
export const PaymentChannelSchema = z.enum([
    'UPI_INTENT',
    'UPI_COLLECT',
    'CARD',
    'NETBANKING',
    'NEFT',
    'OFFLINE',
]);
export const PaymentStatusSchema = z.enum([
    'CREATED',
    'PENDING',
    'AUTHORIZED',
    'CAPTURED',
    'FAILED',
    'REFUNDED',
    'CANCELLED',
]);
/**
 * Data-subject request types under the DPDP Act 2023.
 *
 * Each carries a statutory clock, which is why they are a first-class type
 * rather than a free-text support ticket.
 */
export const DsrTypeSchema = z.enum(['ACCESS', 'CORRECTION', 'ERASURE', 'PORTABILITY']);
export const DsrStatusSchema = z.enum([
    'RECEIVED',
    'IN_PROGRESS',
    'COMPLETED',
    'REJECTED',
    'OVERDUE',
]);
/** The four clients that can hold a session. */
export const PlatformSchema = z.enum([
    'ADMIN_WEB',
    'MOBILE_RESIDENT',
    'MOBILE_GUARD',
    'PLATFORM_WEB',
]);
// ── Shared value objects ────────────────────────────────────────────────────
/**
 * An Indian mobile number in E.164.
 *
 * Ten digits starting 6–9, prefixed `+91`. Landlines and the 1800 series are
 * rejected because an OTP cannot reach them, and a signup that silently fails
 * at the SMS step is worse than one that fails at the field.
 */
export const PhoneSchema = z
    .string()
    .refine((value) => isValidPhoneNumber(value), 'Enter a valid phone number with its country code.');
/** @deprecated Use {@link PhoneSchema}. Numbers are no longer India-only. */
export const IndianPhoneSchema = PhoneSchema;
export const PincodeSchema = z.string().regex(/^[1-9]\d{5}$/, 'Enter a valid 6-digit pincode');
/**
 * An Indian vehicle registration, normalised without spaces.
 *
 * Covers the current `MH12KX4422` format and the older `DL1CAA1111`. BH-series
 * plates (`22BH1234AA`) are matched by the second branch.
 */
export const VehiclePlateSchema = z
    .string()
    .transform((value) => value.replace(/\s+/g, '').toUpperCase())
    .refine((value) => /^[A-Z]{2}\d{1,2}[A-Z]{1,3}\d{1,4}$/.test(value) || /^\d{2}BH\d{4}[A-Z]{1,2}$/.test(value), 'Enter a valid registration number');
/** UUID v7 — the primary key type across every table. */
export const IdSchema = z.string().uuid();
/**
 * A money amount as it crosses the wire, with its unit named.
 *
 * The suffix is not decoration: `amount: 425000` is ambiguous and
 * `amountPaise: 425000` is not, and that ambiguity is exactly where a
 * hundred-fold billing error comes from.
 */
export const MoneySchema = z.object({
    amountPaise: NonNegativePaiseSchema,
    currency: z.literal('INR').default('INR'),
});
/** Standard list query, accepted by every paginated endpoint. */
export const ListQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    q: z.string().trim().min(1).max(120).optional(),
});
/**
 * The `Idempotency-Key` header.
 *
 * Required on every mutating request. A client generates one UUID v4 per
 * logical attempt and reuses it across retries; the server returns the first
 * result rather than doing the work twice.
 */
export const IdempotencyKeySchema = z.string().uuid('Idempotency-Key must be a UUID');
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';
export const TENANT_HEADER = 'X-Society-Id';
export const REQUEST_ID_HEADER = 'X-Request-Id';
//# sourceMappingURL=domain.js.map